import React, { useContext, useMemo } from 'react';
import { Box, Button, Typography, useTheme, useMediaQuery } from '@mui/material';
import { evaluateVisibilityCondition } from '../../utils/visibilityCondition';
import { normalizeInterviewPhases } from '../../utils/interviewFlowRuntime';
import DashboardContext from '../../contexts/DashboardContext';
import AddIcon from '@mui/icons-material/Add';
import DashboardPanel from './DashboardPanel';
import { componentNeedsFlow } from './controlRegistry';
import DashboardGridLayout from './DashboardGridLayout';
import DashboardSummary from './DashboardSummary';
import AppBuilderDropZone from './AppBuilderDropZone';
import DebugPanel from '../AgenticFlow/DebugPanel';

function AddPanelZone({ position, label, onAdd, onDrop }) {
  if (onDrop) {
    return (
      <AppBuilderDropZone position={position} label={label} onDrop={onDrop} onAdd={onAdd}>
        <Button size="small" startIcon={<AddIcon />} onClick={() => onAdd(position)} fullWidth sx={{ py: 1.5 }}>
          {label}
        </Button>
      </AppBuilderDropZone>
    );
  }
  return (
    <Box
      sx={{
        flex: '0 0 auto',
        minHeight: 56,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '2px dashed',
        borderColor: 'divider',
        borderRadius: 1,
        m: 0.5,
        bgcolor: 'action.hover',
      }}
    >
      <Button size="small" startIcon={<AddIcon />} onClick={() => onAdd(position)} fullWidth sx={{ py: 1.5 }}>
        {label}
      </Button>
    </Box>
  );
}

/** True when the panel is driven by shared/structured bindings (no per-panel flow required). */
function hasBindingDataSource(panel) {
  const src = panel?.source;
  if (!src || typeof src !== 'object') return false;
  if (String(src.outputPath || '').trim()) return true;
  const bindings = Array.isArray(src.bindings) ? src.bindings : [];
  return bindings.some((b) => String(b?.sourcePath || '').trim() && String(b?.targetKey || '').trim());
}

/** In runtime, component panels without a flow are hidden — unless they have static UI or binding sources. */
function shouldShowPanel(panel, editMode) {
  if (editMode) return true;
  if (panel?.type === 'chat' || panel?.type === 'summary') return true;
  if (panel?.type === 'component' && !componentNeedsFlow(panel.componentType)) return true;
  // Static intake forms and interactive maps are UI-first (preview demos, address pickers).
  if (panel?.componentType === 'form'
    && Array.isArray(panel?.config?.formFields)
    && panel.config.formFields.length > 0) {
    return true;
  }
  if (panel?.componentType === 'map'
    && (panel?.config?.interactivePick || panel?.config?.interactive)) {
    return true;
  }
  // Citizen / publicAdvise panels (checklist, summary, parcel rich text) bind to shared
  // structuredOutput / panelOutputs without a flowRef — still show them.
  if (hasBindingDataSource(panel)) return true;
  return Boolean(panel?.flowId || panel?.flow_id || panel?.source?.flowRef);
}

/** Hide phase-scoped panels when the active interview phase does not match.
 *  `interviewPhase` may be a string or string[] (panel visible in multiple phases). */
function passesInterviewPhase(panel, interviewPhaseId) {
  const phase = panel?.config?.interviewPhase;
  if (phase == null || phase === '') return true;
  const active = interviewPhaseId ? String(interviewPhaseId) : 'screening';
  const allowed = Array.isArray(phase)
    ? phase.map((p) => String(p))
    : String(phase).split(',').map((p) => p.trim()).filter(Boolean);
  return allowed.includes(active);
}

/** Check conditional visibility (e.g. show panel only when selectedRow exists) */
function passesVisibilityCondition(panel, context) {
  const condition = panel?.config?.visibilityCondition;
  if (!condition) return true;
  return evaluateVisibilityCondition(condition, context);
}

export default function DashboardLayout({ config, darkMode, onLayoutChange, editMode, onPanelClick, onAddPanel, onDropPanel, selectedPanelId }) {
  const theme = useTheme();
  const isSFF = useMediaQuery(theme.breakpoints.down('md'));
  const dashboardContext = useContext(DashboardContext);
  const citizenMode = Boolean(
    config?.citizenMode
    || config?.metadata?.interviewFlow?.citizenMode
    || dashboardContext?.config?.citizenMode
    || dashboardContext?.config?.metadata?.interviewFlow?.citizenMode,
  );
  const interviewPhasesForVisibility = useMemo(
    () => normalizeInterviewPhases(dashboardContext?.config?.metadata?.interviewFlow?.phases),
    [dashboardContext?.config?.metadata?.interviewFlow?.phases],
  );
  const visibilityContext = dashboardContext ? {
    selectedRow: dashboardContext.selectedRow,
    form_prefill: dashboardContext.structuredOutput?.form_prefill,
    panelOutputs: dashboardContext.panelOutputs,
    panelContext: dashboardContext.panelContext,
    interviewPhaseId: dashboardContext.interviewPhaseId,
    sharedTranscript: dashboardContext.sharedTranscript,
    interviewSession: {
      phases: interviewPhasesForVisibility,
      currentPhaseId: dashboardContext.interviewPhaseId,
      currentPhase: interviewPhasesForVisibility.find((p) => p.id === dashboardContext.interviewPhaseId) || null,
    },
  } : {};
  const allPanels = config?.panels ?? [];
  let panels = editMode ? allPanels : allPanels.filter((p) => shouldShowPanel(p, editMode));
  if (!editMode && dashboardContext) {
    panels = panels.filter((p) => passesInterviewPhase(p, dashboardContext.interviewPhaseId));
    panels = panels.filter((p) => passesVisibilityCondition(p, visibilityContext));
  }
  const leftPanels = panels.filter((p) => p.position === 'left').sort((a, b) => (a.order || 0) - (b.order || 0));
  const center = panels.find((p) => p.position === 'center');
  const rightPanels = panels.filter((p) => p.position === 'right').sort((a, b) => (a.order || 0) - (b.order || 0));
  const chatInHeader = config?.chatPosition === 'header';
  const centerIsChat = center?.type === 'chat';
  const topPanels = panels.filter((p) => p.position === 'top').sort((a, b) => (a.order || 0) - (b.order || 0));
  const bottomPanels = panels.filter((p) => p.position === 'bottom').sort((a, b) => (a.order || 0) - (b.order || 0));

  const layout = config?.layout || 'three-panel';
  const isTwoPanel = layout === 'two-panel';

  if (layout === 'grid' && !isSFF) {
    const gridConfig = { ...config, panels };
    return (
      <DashboardGridLayout
        config={gridConfig}
        darkMode={darkMode}
        onLayoutChange={onLayoutChange}
        editMode={editMode}
        onPanelClick={onPanelClick}
        selectedPanelId={selectedPanelId}
      />
    );
  }

  // dynamic + two-panel stack every left/right panel; classic three-panel shows one per column.
  const stackSidePanels = layout === 'dynamic' || layout === 'two-panel';
  const leftColPanels = stackSidePanels ? leftPanels : leftPanels.slice(0, 1);
  const rightColPanels = stackSidePanels ? rightPanels : rightPanels.slice(0, 1);
  const wrapPanel = (panel, isCenter = false, colPanelCount = 1) => {
    const useSummary = isCenter && chatInHeader && (centerIsChat || panel?.type === 'summary') && !editMode;
    const aloneInColumn = stackSidePanels && !isCenter && colPanelCount <= 1;
    const isMap = panel?.componentType === 'map';
    // 2 panels (map + constraints): split height, each scrolls internally — no column scrollbar.
    // Citizen: skip dual inner scrolls; natural heights + one column/page scroll.
    const splitColumn = !citizenMode && stackSidePanels && !isCenter && colPanelCount === 2;
    const fillColumn = !citizenMode && aloneInColumn;
    const stackedSx = stackSidePanels && !isCenter
      ? (citizenMode
        ? {
          // LFF: tall map (~half column). Tables need a floor height so the grid isn't clipped.
          flex: isMap ? '1 1 55%' : (panel?.componentType === 'table' ? '1 1 auto' : '0 0 auto'),
          minHeight: isMap ? 320 : (panel?.componentType === 'table' ? 220 : 0),
          height: isMap ? 'min(50vh, 420px)' : 'auto',
          maxHeight: isMap ? 'min(65vh, 520px)' : undefined,
          overflow: isMap ? 'hidden' : 'visible',
          display: 'flex',
          flexDirection: 'column',
          borderBottom: 1,
          borderColor: 'divider',
        }
        : fillColumn
          ? {
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }
          : splitColumn
            ? {
              flex: isMap ? '1 1 48%' : '1 1 52%',
              minHeight: 0,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              borderBottom: 1,
              borderColor: 'divider',
            }
            : {
              flex: '0 0 auto',
              minHeight: isMap ? 220 : 0,
              height: isMap ? 'min(40vh, 320px)' : 'auto',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              borderBottom: 1,
              borderColor: 'divider',
            })
      : {
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
        display: 'flex',
      };
    // Citizen: map scrolls inside; tables need a real body height so DataGrid isn't clipped.
    const panelBodyScroll = citizenMode
      ? (isMap || panel?.componentType === 'table')
      : (!stackSidePanels || isCenter || fillColumn || splitColumn || isMap);
    const content = useSummary ? (
      <DashboardSummary />
    ) : (
      <DashboardPanel
        panel={panel}
        darkMode={darkMode}
        bodyScroll={panelBodyScroll}
      />
    );
    if (editMode && onPanelClick && !isCenter) {
      const selected = selectedPanelId === panel.id;
      return (
        <Box
          key={panel.id}
          onClick={() => onPanelClick(panel)}
          sx={{
            ...stackedSx,
            cursor: 'pointer',
            outline: selected ? '2px solid' : '1px solid transparent',
            outlineColor: 'primary.main',
            borderRadius: 1,
            m: 0.5,
            transition: 'outline-color 0.2s, box-shadow 0.2s',
            '&:hover': editMode ? { boxShadow: 2, bgcolor: 'action.hover' } : {},
          }}
        >
          {content}
        </Box>
      );
    }
    return (
      <Box key={panel.id} sx={stackedSx}>
        {content}
      </Box>
    );
  };

  const panelColumnSx = {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    overscrollBehavior: citizenMode ? 'auto' : 'contain',
    WebkitOverflowScrolling: 'touch',
    gap: 0,
  };

  const stackedPanels = (layout === 'grid'
    ? [...topPanels, ...leftPanels, ...(center ? [center] : chatInHeader ? [{ id: 'summary', position: 'center', type: 'summary' }] : []), ...rightPanels, ...bottomPanels]
    : [
        ...topPanels,
        ...leftColPanels,
        ...(!isTwoPanel && (center || (chatInHeader && !center)) ? [chatInHeader ? (center || { id: 'summary', position: 'center', type: 'summary' }) : center] : []),
        ...rightColPanels,
        ...bottomPanels,
      ]
  ).filter(Boolean);

  const hasLeft = leftColPanels.length > 0 || (editMode && onAddPanel);
  const hasRight = rightColPanels.length > 0 || (editMode && onAddPanel);
  const hasCenter = !isTwoPanel && (center || (chatInHeader && !center));
  const centerOnly = !editMode && !hasLeft && !hasRight && hasCenter;

  const desktopMainContent = centerOnly ? (
    <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex' }}>
      {wrapPanel(chatInHeader ? (center || { id: 'summary', position: 'center', type: 'summary' }) : center, true)}
    </Box>
  ) : (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: isTwoPanel
          ? (rightColPanels.some((p) => p?.componentType === 'map')
            ? 'minmax(320px, 0.95fr) minmax(360px, 1.15fr)'
            : '1fr 1fr')
          : layout === 'dynamic'
            ? `${Math.max(leftColPanels.length, 1)}fr minmax(320px, 1.2fr) ${Math.max(rightColPanels.length, 1)}fr`
            : '1fr minmax(min(320px, 100%), 1.2fr) 1fr',
        gridTemplateRows: '1fr',
        gap: 0,
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      <Box sx={{
        ...panelColumnSx,
        // Citizen desktop: column scrolls as one (panels sized naturally).
        overflowY: citizenMode ? 'auto' : (leftColPanels.length <= 2 ? 'hidden' : 'auto'),
      }}
      >
        {leftColPanels.map((panel) => wrapPanel(panel, false, leftColPanels.length))}
        {editMode && onAddPanel && (
          <AddPanelZone position="left" label="Add panel (left)" onAdd={onAddPanel} onDrop={onDropPanel} />
        )}
      </Box>
      {hasCenter && (
        <Box sx={{ display: 'flex', minHeight: 0, overflow: 'hidden' }}>
          {wrapPanel(chatInHeader ? (center || { id: 'summary', position: 'center', type: 'summary' }) : center, true, 1)}
        </Box>
      )}
      <Box sx={{
        ...panelColumnSx,
        overflowY: citizenMode ? 'auto' : (rightColPanels.length <= 2 ? 'hidden' : 'auto'),
      }}
      >
        {rightColPanels.map((panel) => wrapPanel(panel, false, rightColPanels.length))}
        {editMode && onAddPanel && (
          <AddPanelZone position="right" label="Add panel (right)" onAdd={onAddPanel} onDrop={onDropPanel} />
        )}
      </Box>
    </Box>
  );

  const mainContent = isSFF ? (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      // Citizen: parent shell scrolls; avoid a second nested bar.
      flex: citizenMode ? '0 0 auto' : 1,
      minHeight: citizenMode ? 'auto' : 0,
      overflow: citizenMode ? 'visible' : 'auto',
      gap: 0,
    }}
    >
      {stackedPanels.map((panel) => (
        <Box
          key={panel.id}
          sx={{
            flex: '0 0 auto',
            minHeight: panel?.componentType === 'map' ? 240 : (citizenMode ? 0 : 160),
            height: panel?.componentType === 'map' ? (citizenMode ? 'min(36vh, 280px)' : 320) : 'auto',
            overflow: 'hidden',
            borderBottom: 1,
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <DashboardPanel
            panel={panel}
            darkMode={darkMode}
            bodyScroll={panel?.componentType === 'map'}
          />
        </Box>
      ))}
      {editMode && onAddPanel && (
        <Box sx={{ flex: '0 0 auto', p: 1 }}>
          <AddPanelZone position="left" label="Add panel" onAdd={onAddPanel} onDrop={onDropPanel} />
        </Box>
      )}
    </Box>
  ) : desktopMainContent;

  const hasTop = topPanels.length > 0 || (editMode && onAddPanel);
  const hasBottom = bottomPanels.length > 0 || (editMode && onAddPanel);

  const debugPanel = !editMode && dashboardContext?.showDebugPanel && dashboardContext?.activeFlowState?.executionId && (
    <DebugPanel
      executionId={dashboardContext.activeFlowState.executionId}
      flowId={dashboardContext.activeFlowState.flowId}
      onClose={dashboardContext.dismissDebugPanel}
      onComplete={dashboardContext.dismissDebugPanel}
    />
  );

  if (isSFF) {
    return (
      <>
        {mainContent}
        {debugPanel}
      </>
    );
  }

  if (hasTop || hasBottom) {
    return (
      <>
        {debugPanel}
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, overflow: 'hidden' }}>
        {hasTop && (
          <Box sx={{ display: 'flex', flex: '0 0 auto', minHeight: 80, maxHeight: '25%', overflowY: 'auto', overflowX: 'hidden', overscrollBehavior: 'contain', borderBottom: 1, borderColor: 'divider', alignItems: 'stretch' }}>
            {topPanels.map((panel) => wrapPanel(panel))}
            {editMode && onAddPanel && (
              <AddPanelZone position="top" label="Add panel (top)" onAdd={onAddPanel} onDrop={onDropPanel} />
            )}
          </Box>
        )}
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
          {mainContent}
        </Box>
        {hasBottom && (
          <Box sx={{ display: 'flex', flex: '0 0 auto', minHeight: 80, maxHeight: '25%', overflowY: 'auto', overflowX: 'hidden', overscrollBehavior: 'contain', borderTop: 1, borderColor: 'divider', alignItems: 'stretch' }}>
            {bottomPanels.map((panel) => wrapPanel(panel))}
            {editMode && onAddPanel && (
              <AddPanelZone position="bottom" label="Add panel (bottom)" onAdd={onAddPanel} onDrop={onDropPanel} />
            )}
          </Box>
        )}
        </Box>
      </>
    );
  }

  return (
    <>
      {mainContent}
      {debugPanel}
    </>
  );
}
